import {
  Home,
  Map,
  ClipboardList,
  Settings2,
  LogOut,
  Menu,
  X,
  Target,
  FileClock,
  MessageCircleWarning,
  ShieldAlert,
  IdCard,
  Users,
  SquareStack,
  RadioTower,
  BadgeCheck,
  Eye,
  ChevronDown,
  ChevronUp,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FeatureProps } from "../interfaces/FeatureProps";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import TopNavigation from "./TopNav";

import {
  useAuthenticate,
  useCan,
  useGetOrganizationId,
  useHasSubscriptions,
  useOrganizationScanners,
  useWolfpacksSensors,
} from "../cacheQueries";
import { useHasRemoteIdPros } from "../hooks/useHasRemoteIdPros";
import { performLogout } from "../auth/logout";
import { clearReturnPath } from "../utils/returnPath";
import {
  getTextSizeClasses,
  getIconSizeClasses,
  getNavItemClasses,
  getNavItemClassesNoHover,
  getSidebarClasses,
} from "../utils/navigationUtils";

import {
  LINK_BASE,
  DIVIDER_BORDER,
  MOBILE_OVERLAY,
  HAMBURGER_BUTTON,
  COLLAPSED_DISPLAY,
  COLLAPSED_RADIUS,
  TEXT_LEFT_GROW,
} from "../constants/navigationClasses";

/* ------------------------- hooks ------------------------- */

const useClickOutside = (
  ref: React.RefObject<HTMLDivElement | null>,
  callback: () => void,
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        callback();
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [ref, callback]);
};

const useBodyScrollLock = (isActive: boolean) => {
  useEffect(() => {
    if (!isActive) return;

    // classList.add with rest args is one mutation per element — fewer
    // style writes than the previous 4× element.style.* assignments per
    // branch and avoids stomping any unrelated inline styles. Tailwind's
    // overflow-hidden + touch-none expand to the same CSS the old code set.
    const body = document.body;
    const html = document.documentElement;
    body.classList.add("overflow-hidden", "touch-none");
    html.classList.add("overflow-hidden", "touch-none");

    return () => {
      body.classList.remove("overflow-hidden", "touch-none");
      html.classList.remove("overflow-hidden", "touch-none");
    };
  }, [isActive]);
};

const useLayoutDetection = (
  ref: React.RefObject<HTMLDivElement | null>,
  dependencies: any[] = [],
) => {
  const [screenSize, setScreenSize] = useState<"small" | "medium" | "large">(
    "medium",
  );

  useEffect(() => {
    const checkLayout = () => {
      // Use viewport height directly for deterministic sizing
      // This avoids the feedback loop where shrinking items removes overflow
      const height = window.innerHeight;
      if (height < 600) setScreenSize("small");
      else if (height <= 800) setScreenSize("medium");
      else setScreenSize("large");
    };

    checkLayout();
    window.addEventListener("resize", checkLayout);
    return () => window.removeEventListener("resize", checkLayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- spread deps are caller-supplied; lint cannot statically analyse the spread
  }, [ref, ...dependencies]);

  // navOverflow is now derived from screenSize, not scroll detection
  // This ensures consistent behavior when resizing
  const navOverflow = screenSize === "small";

  return { navOverflow, screenSize };
};

/**
 * Returns the FIXED sidebar height in PX based on the *visible* viewport.
 * This is the key to not being behind the browser UI.
 */
const useMobileVisiblePanelHeightPx = (
  enabled: boolean,
  topOffsetPx: number,
) => {
  const [heightPx, setHeightPx] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setHeightPx(null);
      return;
    }

    const vv = window.visualViewport;

    const update = () => {
      const visibleH = vv?.height ?? window.innerHeight;
      const h = Math.max(0, Math.floor(visibleH - topOffsetPx));
      setHeightPx(h);
    };

    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [enabled, topOffsetPx]);

  return heightPx;
};

/**
 * Pixels at the bottom currently occluded/covered by browser UI.
 * Used only as EXTRA padding for the footer when needed.
 */
const useBottomOcclusionPx = (enabled: boolean) => {
  const [px, setPx] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setPx(0);
      return;
    }

    const vv = window.visualViewport;
    if (!vv) {
      setPx(0);
      return;
    }

    const update = () => {
      const occluded = Math.max(
        0,
        Math.round(window.innerHeight - (vv.height + vv.offsetTop)),
      );
      setPx(occluded > 8 ? occluded : 0);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [enabled]);

  return px;
};

/* ------------------------- components ------------------------- */

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  className?: string;
  alwaysExpanded?: boolean;
  navOverflow: boolean;
  screenSize: "small" | "medium" | "large";
  isDesktop: boolean;
  location: { pathname: string };
  selectedStyle: object;
  children?: React.ReactNode;
}

const NavItem = ({
  to,
  icon,
  label,
  className = "",
  alwaysExpanded,
  navOverflow,
  screenSize,
  isDesktop,
  location,
  selectedStyle,
  children,
}: NavItemProps) => {
  const isCurrentPath = location.pathname === to;
  const shouldUseCollapsedRadius =
    alwaysExpanded !== undefined ? (alwaysExpanded ? true : true) : false;

  return (
    <div
      className={`${getNavItemClasses(navOverflow, screenSize)} ${className}`}
    >
      <Link
        style={{
          width:
            alwaysExpanded !== undefined
              ? alwaysExpanded
                ? "100%"
                : "100%"
              : undefined,
          ...(isCurrentPath && alwaysExpanded !== undefined && !isDesktop
            ? (selectedStyle as any)
            : null),
        }}
        to={to}
      >
        <div
          className={`${LINK_BASE} ${
            shouldUseCollapsedRadius ? "items-center" : "justify-start"
          }`}
          style={shouldUseCollapsedRadius ? (COLLAPSED_RADIUS as any) : {}}
        >
          {icon}
          <p
            className={shouldUseCollapsedRadius ? TEXT_LEFT_GROW : ""}
            style={
              alwaysExpanded !== undefined
                ? alwaysExpanded
                  ? (COLLAPSED_DISPLAY as any)
                  : {}
                : {}
            }
          >
            {label}
            {children}
          </p>
        </div>
      </Link>
    </div>
  );
};

const SpecialNavItem = ({
  onClick,
  to,
  icon,
  label,
  navOverflow,
  screenSize,
  className = "",
  type = "link",
  textColor = "text-zinc-700 dark:text-white",
}: {
  onClick?: () => void;
  to: string;
  icon: React.ReactNode;
  label: string;
  navOverflow: boolean;
  screenSize: "small" | "medium" | "large";
  className?: string;
  type?: string;
  textColor?: string;
}) => {
  return (
    <div
      className={`${className} ${getTextSizeClasses(
        navOverflow,
        screenSize,
      )} font-primary`}
    >
      <Link
        onClick={onClick}
        to={to}
        type={type as any}
        className={`flex items-center justify-start p-2 space-x-2 transition-colors duration-200 ease-out ${textColor} ${getTextSizeClasses(
          navOverflow,
          screenSize,
        )} font-primary`}
      >
        {icon}
        <span
          className={`${getTextSizeClasses(
            navOverflow,
            screenSize,
          )} font-primary`}
        >
          {label}
        </span>
      </Link>
    </div>
  );
};

export default function FeatureNavTW(featureProps: FeatureProps) {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 1000px)");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [open, setOpen] = useState(false);
  const [remoteIdProOpen, setRemoteIdProOpen] = useState(false);

  // Sidebar collapsed state — icons-only on desktop. Persists per-device
  // via localStorage so it survives reloads. Initialised lazily so SSR /
  // first paint don't trigger a localStorage read mid-render.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem('sidebarCollapsed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        'sidebarCollapsed',
        isSidebarCollapsed ? '1' : '0',
      );
    } catch {
      // Private mode / quota — best-effort only.
    }
    // Toggle a body class so global CSS can override the `lg:ml-64` /
    // `lg:w-[calc(100vw-256px)]` page-margin rules used across the app
    // without us having to touch every page.
    document.body.classList.toggle('sidebar-collapsed', isSidebarCollapsed);

    // Notify map components + any other consumers listening on window
    // resize that the available viewport width has changed. The sidebar
    // transition is 200ms; fire after that lands so Mapbox picks up the
    // final dimensions rather than the mid-animation size.
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 220);

    return () => {
      window.clearTimeout(timer);
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [isSidebarCollapsed]);

  // Sub-nav (Remote ID Pro children) stays visible while the sidebar is
  // collapsed — having to expand the sidebar just to reach Flight Log /
  // Remote ID Pro Units / Drones is a worse experience than seeing them
  // as a small vertical stack of icons.
  // Nav visibility is gated per CAPABILITY, not per role. Each item asks for
  // the permission its page actually needs, so an operator — who holds every
  // configuration capability but not member management — sees everything except
  // Users, and a future role rearrangement moves these items automatically.
  //
  // Re-evaluates on org switch: switching organizations hard-reloads the page,
  // so the session (and its capability list) is resolved afresh for the new org.
  //
  // These only hide links. Every page behind them is independently enforced
  // server-side; the route guard in main.tsx covers someone typing the URL.
  const canManageZones = useCan("zones:write");
  const canManageAlertRules = useCan("alert-rules:write");
  const canManageWatchlist = useCan("watchlist:write");
  const canManageApprovedDrones = useCan("approved-drones:write");
  const canManageDevices = useCan("devices:manage");
  const canManageMembers = useCan("members:manage");
  const [userId, setUserId] = useState<string | null>(null);
  const [ShowEditOrganizationForm, setShowEditOrganizationForm] =
    useState<boolean>(true);

  const outerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Auto-close the mobile drawer on every route change. Without this, tapping
  // a nav item navigates but leaves the off-canvas drawer covering the new
  // page until the user dismisses it manually. Route-change-based is
  // preferable to per-Link onClick handlers because it covers every current
  // and future nav entry — including indirect navigations (a button inside
  // a page that calls navigate(), the Bell notification click-to-view flow,
  // etc.). Skipped above lg where the drawer is the persistent desktop
  // sidebar.
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on ROUTE CHANGE only; with `open` in deps the drawer would close the moment it opens
  }, [location.pathname]);

  const clientID = import.meta.env.VITE_CLIENT_ID;
  const selectedStyle = {};

  const hasRemoteIdPros = useHasRemoteIdPros();
  const { data: user, isLoading: userLoading } = useAuthenticate();

  // Use master's derived state pattern for cleaner code
  const organizationScannersQuery = useOrganizationScanners();
  const hasSubscriptionsQuery = useHasSubscriptions();

  const organizationScanners = organizationScannersQuery.data ?? [];
  const hasSubscriptionsData = hasSubscriptionsQuery.data;

  // `status === "pending"` is a genuine first load, with no data yet.
  // `fetchStatus === "fetching"` is ALSO true for a background refetch of data
  // we already have, and useOrganizationScanners / useAuthenticate both carry
  // refetchOnWindowFocus, so counting it as loading hid the whole nav every
  // time the tab regained focus. That was half the flash.
  const subscriptionsPending = hasSubscriptionsQuery.status === "pending";
  const scannersPending = organizationScannersQuery.status === "pending";

  const hasSubscriptions = Array.isArray(hasSubscriptionsData)
    ? hasSubscriptionsData.length > 0
    : hasSubscriptionsData === true;
  const hasScanners = organizationScanners.length > 0;

  // The other half: the focus/pageshow re-probe of ["authenticate"] flaps
  // user.uid, so useGetOrganizationId's key changes and every org-keyed query
  // re-pends for a tick. AppLayout absorbs this for the routed page with its
  // lastOrgRef; the sidebar renders outside that guard and has to do its own.
  //
  // Hold the last resolved answer while the context is unresolved. A real org
  // switch still lands: it resolves to that org's data and replaces this. A
  // flap resolves back to the same answer and nobody sees it move.
  const { data: organizationId } = useGetOrganizationId();
  const navResolving =
    organizationId == null || subscriptionsPending || scannersPending;
  const resolvedNav = {
    features: hasSubscriptions || hasScanners,
    scanners: hasScanners,
  };
  const lastNavRef = useRef(resolvedNav);
  if (!navResolving) lastNavRef.current = resolvedNav;
  const nav = navResolving ? lastNavRef.current : resolvedNav;

  const subscriptionOrScannerFeaturesAvailable = nav.features;
  const scannersAvailable = nav.scanners;

  const { data: wolfpacks } = useWolfpacksSensors();

  useClickOutside(outerRef, () => setOpen(false));
  useBodyScrollLock(open);

  const { navOverflow, screenSize } = useLayoutDetection(scrollRef, [
    hasSubscriptions,
    hasScanners,
    featureProps.handleHistory,
  ]);

  // top-12 == 3rem == 48px (matches getSidebarClasses calc(100vh - 3rem))
  const mobilePanelHeightPx = useMobileVisiblePanelHeightPx(isMobile, 48);
  const bottomOcclusionPx = useBottomOcclusionPx(isMobile);

  useEffect(() => {
    if (!userLoading && user && !userId) setUserId(user.uid);
  }, [userLoading, user, userId]);

  // Expand Remote ID Pro by default when user has airplates but no subscription/scanner features
  useEffect(() => {
    if (hasRemoteIdPros && !subscriptionOrScannerFeaturesAvailable) {
      setRemoteIdProOpen(true);
    }
  }, [hasRemoteIdPros, subscriptionOrScannerFeaturesAvailable]);

  const navEl = (
    <div
      ref={outerRef}
      data-collapsed={isSidebarCollapsed ? 'true' : 'false'}
      className={`
        ${getSidebarClasses(
          clientID,
          navOverflow,
          open,
          featureProps.alwaysExpanded ?? false,
        )}
        ${isSidebarCollapsed ? 'lg:!w-20' : ''}
        overflow-hidden select-none
      `}
      // IMPORTANT: override the 100vh-based height from getSidebarClasses on mobile
      style={
        isMobile && mobilePanelHeightPx !== null
          ? {
              height: `${mobilePanelHeightPx}px`,
              maxHeight: `${mobilePanelHeightPx}px`,
              overflow: "hidden",
            }
          : undefined
      }
    >
      {/* Scrollable content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {!featureProps.alwaysExpanded ? (
          <img className="hidden" src="./images/AirPlate-Logo.svg" />
        ) : null}

        {/* Dashboard row + collapse toggle.
            Expanded (256px sidebar): toggle is absolutely positioned at
              the right edge of the Dashboard row so it shares the row
              visually but doesn't compete for click area with the link.
            Collapsed (64px sidebar): toggle moves into its own row above
              the Dashboard icon — there isn't room to keep both side by
              side without overlap.
            Both cases hide the toggle on mobile (`hidden lg:flex` /
              `lg:block`) since the burger in TopNav handles drawer
              open/close there. */}
        {isSidebarCollapsed ? (
          <div
            className={`hidden lg:block ${getNavItemClasses(
              navOverflow,
              screenSize,
            )}`}
          >
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed((v) => !v)}
              aria-label={t('nav.expandSidebar', 'Expand sidebar')}
              title={t('nav.expandSidebar', 'Expand sidebar')}
              className={`${LINK_BASE} w-full rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary`}
              style={COLLAPSED_RADIUS as any}
            >
              <PanelLeftOpen
                className={getIconSizeClasses(navOverflow, screenSize)}
                strokeWidth={1.75}
              />
            </button>
          </div>
        ) : null}
        <div className="relative">
          <NavItem
            to="/"
            icon={
              <Home className={getIconSizeClasses(navOverflow, screenSize)} />
            }
            label={t('nav.dashboard', 'Dashboard')}
            alwaysExpanded={featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
          />
          {!isSidebarCollapsed ? (
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed((v) => !v)}
              aria-label={t('nav.collapseSidebar', 'Collapse sidebar')}
              title={t('nav.collapseSidebar', 'Collapse sidebar')}
              className="hidden lg:flex absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 items-center justify-center rounded-md text-zinc-600 transition-colors hover:bg-zinc-200 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary dark:text-zinc-300 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <PanelLeftClose className="h-5 w-5" strokeWidth={1.75} />
            </button>
          ) : null}
        </div>

        {subscriptionOrScannerFeaturesAvailable ? (
          <NavItem
            to="/map"
            icon={
              <Map className={getIconSizeClasses(navOverflow, screenSize)} />
            }
            label={t('nav.map', 'Map')}
            alwaysExpanded={featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
          />
        ) : null}

        {subscriptionOrScannerFeaturesAvailable ? (
          <NavItem
            to="/intrusionlog"
            icon={
              <FileClock
                className={getIconSizeClasses(navOverflow, screenSize)}
              />
            }
            label={t('nav.intrusionLog', 'Intrusion Log')}
            alwaysExpanded={featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
          />
        ) : null}

        {subscriptionOrScannerFeaturesAvailable ? (
          <NavItem
            to="/heatmap"
            icon={
              <Target className={getIconSizeClasses(navOverflow, screenSize)} />
            }
            label={t('nav.heatmap', 'Heatmap')}
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
          />
        ) : null}

        {subscriptionOrScannerFeaturesAvailable && canManageZones ? (
          <NavItem
            to="/alarmzones"
            icon={
              <ShieldAlert
                className={getIconSizeClasses(navOverflow, screenSize)}
              />
            }
            label={t('nav.alarmZones', 'Alarm Zones')}
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
          />
        ) : null}

        {subscriptionOrScannerFeaturesAvailable && canManageAlertRules ? (
          <NavItem
            to="/smsnotifications"
            icon={
              <MessageCircleWarning
                className={getIconSizeClasses(navOverflow, screenSize)}
              />
            }
            label={t('nav.smsAlerts', 'Alerts')}
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
          />
        ) : null}

        {subscriptionOrScannerFeaturesAvailable && canManageWatchlist ? (
          <NavItem
            to="/watchlist"
            icon={<Eye className={getIconSizeClasses(navOverflow, screenSize)} />}
            label={t('nav.watchlistDrones', 'Watchlist')}
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
            className="border-solid border-0 rounded-md m-1 hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700"
          />
        ) : null}

        {subscriptionOrScannerFeaturesAvailable && canManageApprovedDrones ? (
          <NavItem
            to="/approveddrones"
            icon={
              <BadgeCheck
                className={getIconSizeClasses(navOverflow, screenSize)}
              />
            }
            label={t('nav.approvedDrones', 'Approved Drones')}
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
            className="border-solid border-0 rounded-md m-1 hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700"
          />
        ) : null}

        {scannersAvailable && canManageDevices ? (
          <NavItem
            to="/configscanners"
            icon={
              <RadioTower
                className={getIconSizeClasses(navOverflow, screenSize)}
              />
            }
            label={
              (wolfpacks as any)?.length > 0 ? t('nav.sensors', 'Sensors') : t('nav.airDetectSystems', 'AirDetect Systems')
            }
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
            className="border-solid border-0 rounded-md m-1 hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700"
          />
        ) : null}

{hasRemoteIdPros ? (
          <div className={getNavItemClassesNoHover(navOverflow, screenSize)}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setRemoteIdProOpen((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRemoteIdProOpen((prev) => !prev);
                }
              }}
              className="block w-full rounded-md outline-none focus:outline-none focus:ring-0 hover:bg-zinc-300 dark:hover:bg-dark-background"
              style={{ padding: 0, margin: 0 }}
            >
              <div
                className={`${LINK_BASE} items-center justify-between`}
                style={COLLAPSED_RADIUS as any}
              >
                {/* Icon + a collapsed-only chevron grouped together, so when
                    the sidebar is collapsed the IdCard reads as a parent
                    group that toggles its sub-items. The chevron flips with
                    `remoteIdProOpen` in both collapsed and expanded modes. */}
                <span className="flex shrink-0 items-center gap-0.5">
                  <IdCard
                    className={getIconSizeClasses(navOverflow, screenSize)}
                  />
                  <span className="nav-only-when-collapsed shrink-0">
                    {remoteIdProOpen ? (
                      <ChevronUp
                        className="h-3 w-3 opacity-50"
                        strokeWidth={2.5}
                      />
                    ) : (
                      <ChevronDown
                        className="h-3 w-3 opacity-50"
                        strokeWidth={2.5}
                      />
                    )}
                  </span>
                </span>
                <p className={TEXT_LEFT_GROW} style={COLLAPSED_DISPLAY}>
                  {t('nav.remoteIdPro', 'Remote ID Pro')}
                </p>
                <span className="nav-only-when-expanded">
                  {remoteIdProOpen ? (
                    <ChevronUp
                      className={getIconSizeClasses(navOverflow, screenSize)}
                    />
                  ) : (
                    <ChevronDown
                      className={getIconSizeClasses(navOverflow, screenSize)}
                    />
                  )}
                </span>
              </div>
            </div>
            {remoteIdProOpen ? (
              <div
                className={
                  isSidebarCollapsed
                    ? "pl-2 pr-1 pb-1 space-y-0.5"
                    : "pl-4 pr-1 pb-1 space-y-0.5"
                }
              >
                <Link
                  to="/flightlogs"
                  className={`flex items-start p-2 rounded-md hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700 dark:text-white ${getTextSizeClasses(
                    navOverflow,
                    screenSize,
                  )} font-primary leading-tight`}
                  onClick={() => isMobile && setOpen(false)}
                >
                  <ClipboardList
                    className={`${getIconSizeClasses(navOverflow, screenSize)} mr-2 mt-0.5 shrink-0`}
                  />
                  <span className="text-left break-words">{t('nav.flightLog', 'Flight Log')}</span>
                </Link>
                {canManageDevices ? (
                  <>
                    <Link
                      to="/configremoteidpros"
                      className={`flex items-start p-2 rounded-md hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700 dark:text-white ${getTextSizeClasses(
                        navOverflow,
                        screenSize,
                      )} font-primary leading-tight`}
                      onClick={() => isMobile && setOpen(false)}
                    >
                      <IdCard
                        className={`${getIconSizeClasses(navOverflow, screenSize)} mr-2 mt-0.5 shrink-0`}
                      />
                      <span className="text-left break-words">{t('nav.remoteIdProUnits', 'Remote ID Pro Units')}</span>
                    </Link>
                    <Link
                      to="/configdrones"
                      className={`flex items-start p-2 rounded-md hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700 dark:text-white ${getTextSizeClasses(
                        navOverflow,
                        screenSize,
                      )} font-primary leading-tight`}
                      onClick={() => isMobile && setOpen(false)}
                    >
                      <SquareStack
                        className={`${getIconSizeClasses(navOverflow, screenSize)} mr-2 mt-0.5 shrink-0`}
                      />
                      <span className="text-left break-words">{t('nav.drones', 'Drones')}</span>
                    </Link>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {canManageMembers ? (
          <NavItem
            to="/configusers"
            icon={
              <Users className={getIconSizeClasses(navOverflow, screenSize)} />
            }
            label={t('nav.users', 'Users')}
            alwaysExpanded={!featureProps.alwaysExpanded}
            navOverflow={navOverflow}
            screenSize={screenSize}
            isDesktop={isDesktop}
            location={location}
            selectedStyle={selectedStyle}
            className="border-solid border-0 rounded-md m-1 hover:bg-zinc-300 dark:hover:bg-dark-background text-zinc-700"
          />
        ) : null}
      </div>

      {/* Footer: always visible + extra padding only when bottom is covered */}
      <div
        className="shrink-0"
        style={{
          paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${
            isMobile ? bottomOcclusionPx : 0
          }px)`,
        }}
      >
        <div className={DIVIDER_BORDER}></div>

        <SpecialNavItem
          onClick={() => setShowEditOrganizationForm(true)}
          to="/accountsettings"
          icon={
            <Settings2
              className={getIconSizeClasses(navOverflow, screenSize)}
            />
          }
          label={t('nav.settings', 'Settings')}
          navOverflow={navOverflow}
          screenSize={screenSize}
          className="border-solid border-0 rounded-md m-1 mb-2 hover:bg-zinc-300 dark:hover:bg-dark-background text-red-600 dark:text-red-500"
        />

        <div
          onClick={() => {
            // Explicit logout: forget any stored post-login return path.
            clearReturnPath();
            void performLogout(navigate, "/airmap");
          }}
          className="hover:cursor-pointer border-solid border-0 rounded-md m-1 mb-2 hover:bg-zinc-300 dark:hover:bg-dark-background"
        >
          <div
            className={`flex items-center justify-start p-2 space-x-2 transition-colors duration-200 ease-out text-red-600 dark:text-red-500 ${getTextSizeClasses(
              navOverflow,
              screenSize,
            )} font-primary`}
          >
            <LogOut className={getIconSizeClasses(navOverflow, screenSize)} />
            {!navOverflow && <span>{t('nav.logout', 'Logout')}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <TopNavigation />
      {navEl}

      {open && <div className={MOBILE_OVERLAY}></div>}

      <button
        className={HAMBURGER_BUTTON}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(!open);
        }}
      >
        {open ? (
          <X className="w-8 h-8 text-white hover:text-zinc-300" />
        ) : (
          <Menu className="w-8 h-8 text-white hover:text-zinc-300" />
        )}
      </button>
    </>
  );
}
